/**
 * Client-side DOCX/PPTX unzip utilities.
 *
 * Extracts only TEXT XMLs needed by server parser (document, charts, diagrams).
 * Excludes media (images, videos), fonts, themes, styles — those stay on client.
 *
 * Reduces upload size by 80-97% for typical files.
 */

import JSZip from 'jszip';

export type OfficeFormat = 'docx' | 'pptx';

/**
 * Patterns for text XMLs that server needs for parsing.
 */
const TEXT_XML_PATTERNS: Record<OfficeFormat, RegExp[]> = {
    docx: [
        /^word\/document\.xml$/,
        /^word\/charts\/chart\d+\.xml$/,
        /^word\/diagrams\/data\d+\.xml$/,
        /^word\/header\d+\.xml$/,
        /^word\/footer\d+\.xml$/,
        /^word\/footnotes\.xml$/,
        /^word\/endnotes\.xml$/,
    ],
    pptx: [
        /^ppt\/slides\/slide\d+\.xml$/,
        /^ppt\/charts\/chart\d+\.xml$/,
        /^ppt\/diagrams\/data\d+\.xml$/,
        /^ppt\/diagrams\/drawing\d+\.xml$/,
        /^ppt\/notesSlides\/notesSlide\d+\.xml$/,
    ],
};

// Max uncompressed size for main XML entry — hard cap để chặn file quá lớn ngay.
// Zip central directory exposes this without decompression (O(1) check).
export const MAX_MAIN_XML_UNCOMPRESSED_BYTES = 15_000_000; // 15 MB

const MAIN_XML_KEY: Record<OfficeFormat, string> = {
    docx: 'word/document.xml',
    pptx: 'ppt/presentation.xml',
};

export class XmlTooLargeError extends Error {
    constructor(public uncompressedBytes: number) {
        super(`XML too large: ${(uncompressedBytes / 1_000_000).toFixed(1)} MB uncompressed`);
        this.name = 'XmlTooLargeError';
    }
}

/**
 * Read the uncompressed size of the main XML entry from ZIP central directory,
 * using only file.slice() — never loads the full file into memory.
 *
 * Reads: EOCD (22 bytes from end) + central directory (~few KB).
 * Does NOT call file.arrayBuffer() — safe to call before any heavy I/O.
 *
 * Returns 0 if format unrecognized or entry not found (caller treats as safe).
 */
export async function peekMainXmlUncompressedSize(file: File, format: OfficeFormat): Promise<number> {
    const mainKey = MAIN_XML_KEY[format];
    if (file.size < 22) return 0;

    // Step 1: Read EOCD — last 22 bytes
    const eocdBuf = await file.slice(file.size - 22).arrayBuffer();
    const eocd = new DataView(eocdBuf);
    if (eocd.getUint32(0, true) !== 0x06054b50) return 0; // bad signature (ZIP comment present — rare)

    const cdSize = eocd.getUint32(12, true);
    const cdOffset = eocd.getUint32(16, true);
    if (cdOffset === 0xffffffff || cdSize === 0) return 0; // ZIP64 — not handled

    // Step 2: Read only central directory
    const cdBuf = await file.slice(cdOffset, cdOffset + cdSize).arrayBuffer();
    const cd = new DataView(cdBuf);
    const mainKeyBytes = new TextEncoder().encode(mainKey);

    let pos = 0;
    while (pos + 46 <= cdBuf.byteLength) {
        if (cd.getUint32(pos, true) !== 0x02014b50) break; // central dir file header signature
        const uncompressedSize = cd.getUint32(pos + 24, true);
        const filenameLen = cd.getUint16(pos + 28, true);
        const extraLen = cd.getUint16(pos + 30, true);
        const commentLen = cd.getUint16(pos + 32, true);

        if (filenameLen === mainKeyBytes.length && pos + 46 + filenameLen <= cdBuf.byteLength) {
            const filename = new TextDecoder().decode(new Uint8Array(cdBuf, pos + 46, filenameLen));
            if (filename === mainKey) return uncompressedSize;
        }
        pos += 46 + filenameLen + extraLen + commentLen;
    }
    return 0;
}

/**
 * Extract only text XMLs from a DOCX/PPTX file buffer.
 * Returns map of path → XML content string.
 *
 * Throws XmlTooLargeError nếu document.xml > MAX_MAIN_XML_UNCOMPRESSED_BYTES (15MB).
 * Check từ ZIP central directory metadata (không cần decompress).
 */
export async function extractTextXmls(
    arrayBuffer: ArrayBuffer,
    format: OfficeFormat,
): Promise<Record<string, string>> {
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Size pre-check from ZIP metadata (no decompression needed)
    const mainKey = MAIN_XML_KEY[format];
    const mainEntry = zip.file(mainKey);
    const uncompressedBytes: number = (mainEntry as any)?._data?.uncompressedSize ?? 0;
    if (uncompressedBytes > MAX_MAIN_XML_UNCOMPRESSED_BYTES) {
        throw new XmlTooLargeError(uncompressedBytes);
    }

    const patterns = TEXT_XML_PATTERNS[format];
    const xmls: Record<string, string> = {};

    for (const path of Object.keys(zip.files)) {
        const entry = zip.files[path];
        if (entry.dir) continue;
        if (patterns.some((p) => p.test(path))) {
            xmls[path] = await entry.async('string');
        }
    }

    return xmls;
}

/**
 * Estimate token count từ extracted XMLs WITHOUT calling server.
 *
 * Strips <w:t> (paragraph runs) + <c:v>/<a:t> (chart labels) text content,
 * counts chars, divides by Vietnamese ratio 2.5 chars/token.
 *
 * Use case: pre-flight 300K token cap check trong modal trước khi gọi
 * /api/parse-docx. Heuristic ~96.7% accurate vs Vertex countTokens (verified
 * scripts/compare-charest-vs-gemini.mts) — slight under-estimate ~3.3%.
 *
 * Returns rough estimated token count (Vietnamese basis).
 */
export function estimateTokensFromXmls(xmls: Record<string, string>): { chars: number; estimatedTokens: number } {
    let totalChars = 0;
    const wtRegex = /<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g;
    const cvRegex = /<c:v>([^<]*)<\/c:v>/g;
    const atRegex = /<a:t(?:\s[^>]*)?>([^<]*)<\/a:t>/g;

    for (const [path, xml] of Object.entries(xmls)) {
        // Strip mc:Fallback duplicates first (parity với parseDocxDirect)
        const noFb = xml.replace(/<mc:Fallback[^>]*>[\s\S]*?<\/mc:Fallback>/g, '');

        // <w:t> body text + headers/footers/footnotes/endnotes
        wtRegex.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = wtRegex.exec(noFb)) !== null) totalChars += m[1].length;

        // Chart labels (data values + titles)
        if (path.includes('/charts/')) {
            cvRegex.lastIndex = 0;
            while ((m = cvRegex.exec(noFb)) !== null) {
                const v = m[1].trim();
                if (v && isNaN(Number(v))) totalChars += v.length;
            }
            atRegex.lastIndex = 0;
            while ((m = atRegex.exec(noFb)) !== null) totalChars += m[1].length;
        }

        // PPTX slides — <a:t> nằm ngoài chart context
        if (path.includes('/slides/') || path.includes('/notesSlides/')) {
            atRegex.lastIndex = 0;
            while ((m = atRegex.exec(noFb)) !== null) totalChars += m[1].length;
        }
    }

    return {
        chars: totalChars,
        estimatedTokens: Math.ceil(totalChars / 2.5),
    };
}

/**
 * Build a minimal zip Blob containing only the given XMLs.
 * Ready to upload via FormData.
 */
export async function buildFilteredZip(xmls: Record<string, string>): Promise<Blob> {
    const zip = new JSZip();
    for (const [path, content] of Object.entries(xmls)) {
        zip.file(path, content);
    }
    return await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 },
    });
}

/**
 * Convenience: unzip file + build filtered zip in one call.
 */
export async function prepareFilteredUpload(
    arrayBuffer: ArrayBuffer,
    format: OfficeFormat,
): Promise<{ xmls: Record<string, string>; filteredZip: Blob }> {
    const xmls = await extractTextXmls(arrayBuffer, format);
    const filteredZip = await buildFilteredZip(xmls);
    return { xmls, filteredZip };
}

/**
 * Detect format from filename extension.
 */
export function detectFormat(fileName: string): OfficeFormat | null {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.docx')) return 'docx';
    if (lower.endsWith('.pptx')) return 'pptx';
    return null;
}
